/* Types */
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import { item, tMenu } from 'src/shared/frontendTypes/navigation'
import { saveUrl } from 'src/functions/regex'

type BreadcrumbTarget = {
  menueId?: number | null
  brandId?: number | null
}

export const cleanNavigationPathPart = (path?: string) =>
  saveUrl((path || '').replace(/^\/+|\/+$/g, ''))

export const hrefForBrandPath = (path: string | undefined, brandId: number | string | undefined) => {
  const cleanPath = cleanNavigationPathPart(path)
  if (!cleanPath || !brandId) return undefined
  return `/b/${cleanPath}/${brandId}`
}

export const hrefForNavigationItem = (menuItem: item, parentBrand: item | null = null) => {
  if (menuItem.href) return menuItem.href

  const path = cleanNavigationPathPart(menuItem.path)
  if (menuItem.brand && path) return hrefForBrandPath(path, menuItem.brand)

  if (menuItem.menueId && path) {
    const brandPath = cleanNavigationPathPart(parentBrand?.path)
    if (parentBrand?.brand && brandPath) {
      return `/b/${brandPath}/${parentBrand.brand}/m/${path}/${menuItem.menueId}`
    }

    return `/m/${path}/${menuItem.menueId}`
  }

  if (menuItem.title === 'Marken') return '/brands'

  return undefined
}

const crumbForItem = (
  menuItem: item,
  parentBrand: item | null,
): BreadcrumbItem | null => {
  const title = String(menuItem.title || '').trim()
  if (!title || title === '0') return null

  return {
    title,
    href: hrefForNavigationItem(menuItem, parentBrand),
  }
}

const findNavigationTrail = (
  items: tMenu,
  target: BreadcrumbTarget,
  trail: BreadcrumbItem[] = [],
  parentBrand: item | null = null,
): BreadcrumbItem[] | null => {
  for (const menuItem of items) {
    const currentCrumb = crumbForItem(menuItem, parentBrand)
    const currentTrail = currentCrumb ? [...trail, currentCrumb] : trail
    const nextParentBrand = menuItem.brand ? menuItem : parentBrand
    const isBrandTarget = Boolean(
      target.brandId
      && !target.menueId
      && menuItem.brand === target.brandId
    )
    const isMenuTarget = Boolean(
      target.menueId
      && menuItem.menueId === target.menueId
      && (!target.brandId || parentBrand?.brand === target.brandId || menuItem.brand === target.brandId)
    )

    if (isBrandTarget || isMenuTarget) return currentTrail

    if (menuItem.children?.length) {
      const childTrail = findNavigationTrail(
        menuItem.children,
        target,
        currentTrail,
        nextParentBrand,
      )

      if (childTrail) return childTrail
    }
  }

  return null
}

export const buildNavigationBreadcrumb = (
  paths: tMenu,
  target: BreadcrumbTarget,
): BreadcrumbItem[] => {
  const directTrail = findNavigationTrail(paths, target)
  if (directTrail) return directTrail

  if (target.brandId && target.menueId) {
    const brandTrail = findNavigationTrail(paths, { brandId: target.brandId })
    const menuTrail = findNavigationTrail(paths, { menueId: target.menueId })

    if (brandTrail && menuTrail) return [
      ...brandTrail,
      ...menuTrail.filter(menuCrumb =>
        !brandTrail.some(brandCrumb =>
          brandCrumb.title === menuCrumb.title && brandCrumb.href === menuCrumb.href
        )
      ),
    ]
  }

  return []
}
